// To be used in exceptionHandler.ts

export type Area = 'platform' | 'operate' | 'DPI' | 'build' | 'advance' | 'connect' | 'improve';

export type Rule =
  | { type: 'exact'; path: string; area: Area }
  | { type: 'startsWith'; prefix: string; area: Area }
  | { type: 'regex'; pattern: RegExp; area: Area };

export const platformRules: Rule[] = [
  { type: 'startsWith', prefix: '/admin/customers', area: 'platform' },
  { type: 'exact', path: '/v3/dashboard/admin', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/admin/customer', area: 'platform' },
  { type: 'exact', path: '/v3/dashboard/admin/employees', area: 'platform' },
  { type: 'exact', path: '/v3/dashboard/admin/metrics', area: 'platform' },
  { type: 'exact', path: '/v3/dashboard/admin/reindex', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/admin/request-access', area: 'platform' },
  { type: 'exact', path: '/v3/dashboard/forgot-password', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/invitation', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/login', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/mfa', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/reset-password', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/verify-customer-email', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/verify-email', area: 'platform' },
  { type: 'startsWith', prefix: '/v3/dashboard/admin/global-services', area: 'platform' },

];

export const operateRules: Rule[] = [
  // exact
  { type: 'exact', path: '/applications', area: 'operate' },
  { type: 'exact', path: '/approvals', area: 'operate' },
  { type: 'exact', path: '/queues', area: 'operate' },
  { type: 'exact', path: '/sars', area: 'operate' },
  { type: 'exact', path: '/v3/dashboard/approvals', area: 'operate' },
  { type: 'exact', path: '/v3/dashboard/audit', area: 'operate' },
  { type: 'exact', path: '/v3/dashboard/portfolio', area: 'operate' },
  { type: 'exact', path: '/v3/dashboard/queues', area: 'operate' },
  { type: 'exact', path: '/v3/dashboard/notifications', area: 'operate' },

  // prefixes
  { type: 'startsWith', prefix: '/evaluations', area: 'operate' },
  { type: 'startsWith', prefix: '/groups', area: 'operate' },
  { type: 'startsWith', prefix: '/investigations', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/cases', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/ctrs', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/evaluations', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/groups', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/reports', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/roles', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/searches', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/case-management', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/published-attributes', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/allowlist', area: 'operate' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/denylist', area: 'operate' },

  // regex: starts with /entities AND does not contain '/portfolio-evaluations' anywhere
  { type: 'regex', pattern: /^(?!.*\/portfolio-evaluations)\/entities(?:\/|$).*/, area: 'operate' },
];

export const dpiRules: Rule[] = [
  { type: 'startsWith', prefix: '/admin/services', area: 'DPI' },
  { type: 'exact', path: '/v3/dashboard/admin/chimera_decrypt', area: 'DPI' },
  { type: 'startsWith', prefix: '/v3/dashboard/admin/decrypt', area: 'DPI' },
  { type: 'exact', path: '/v3/dashboard/admin/services', area: 'DPI' },
  { type: 'startsWith', prefix: '/v3/dashboard/partner-solutions', area: 'DPI' },
];

export const buildRules: Rule[] = [
  { type: 'startsWith', prefix: '/v3/dashboard/journeys', area: 'build' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/tags', area: 'build' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/outcomes', area: 'build' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/review-reasons', area: 'build' },


  // regex: starts with /applications … ends with /versions/create
  {
    type: 'regex',
    pattern: /^\/applications(?:\/.*)?\/versions\/create$/,
    area: 'build',
  },

  { type: 'startsWith', prefix: '/workflows', area: 'build' },
];

export const connectRules: Rule[] = [
  { type: 'startsWith', prefix: '/v3/dashboard/settings/webhooks', area: 'connect' },
  { type: 'startsWith', prefix: '/v3/dashboard/settings/webhook-logs', area: 'connect' },


];

export const advanceRules: Rule[] = [
  // contains /portfolio-evaluations
  { type: 'regex', pattern: /\/portfolio-evaluations(?:\/|$)/, area: 'advance' },

  // contains /what-if
  { type: 'regex', pattern: /\/what-if(?:\/|$)/, area: 'advance' },

  // ends with completed-results
  { type: 'regex', pattern: /(?:^|\/)completed-results$/, area: 'advance' },

  // starts with /workflow
  { type: 'startsWith', prefix: '/workflow', area: 'advance' },

  // contains /workflows and a path segment that begins with CT-
  // (somewhere after /workflows, there’s a segment like /CT-XXXX)
  { type: 'regex', pattern: /\/workflows(?:\/[^/]*)*\/CT-[^/]+(?:\/|$)/, area: 'advance' },
];

export const improveRules: Rule[] = [
  { type: 'startsWith', prefix: '/v3/dashboard/home', area: 'improve' },
];
